import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { reviews } from '../../data/reviews';

const ReviewCarousel = ({ compact = false }) => {
    const [index, setIndex] = useState(0);
    const theme = useTheme();

    // Media queries for different screen heights (mobile responsiveness)
    const isVerySmallMobile = useMediaQuery('(max-height: 600px)');
    const isSmallMobile = useMediaQuery('(max-height: 700px)');
    const isMediumMobile = useMediaQuery('(max-height: 850px)');

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((prev) => (prev + 1) % reviews.length);
        }, 12000); // Increased to 12 seconds
        return () => clearInterval(timer);
    }, []);

    const currentReview = reviews[index];

    const renderText = (text, highlight) => {
        if (!highlight || !text.includes('{highlight}')) {
            return text;
        }

        const parts = text.split('{highlight}');
        return (
            <>
                {parts[0]}
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        color: '#0051C3',
                        borderBottom: '2.5px solid #0051C3',
                        px: 0.2,
                        mx: 0.5,
                        fontWeight: 700
                    }}
                >
                    {highlight}
                </Box>
                {parts[1]}
            </>
        );
    };

    // Dynamic responsive values based on screen height for compact mode
    const getCompactStyles = () => {
        if (!compact) return null;

        if (isVerySmallMobile) {
            return {
                containerHeight: '140px',  // Increased to fit content
                avatarSize: 32,
                reviewFontSize: '0.65rem',
                nameFontSize: '0.7rem',
                titleFontSize: '0.6rem',
                quoteFontSize: '1rem',
                quoteOffset: -6,
                lineHeight: 1.3,
                padding: 1,
                paddingX: 2
            };
        } else if (isSmallMobile) {
            return {
                containerHeight: '160px',  // Increased to fit content
                avatarSize: 36,
                reviewFontSize: '0.7rem',
                nameFontSize: '0.75rem',
                titleFontSize: '0.65rem',
                quoteFontSize: '1.2rem',
                quoteOffset: -7,
                lineHeight: 1.35,
                padding: 1.5,
                paddingX: 2
            };
        } else if (isMediumMobile) {
            return {
                containerHeight: '180px',  // Increased to fit content
                avatarSize: 42,
                reviewFontSize: '0.75rem',
                nameFontSize: '0.8rem',
                titleFontSize: '0.7rem',
                quoteFontSize: '1.4rem',
                quoteOffset: -8,
                lineHeight: 1.4,
                padding: 2,
                paddingX: 3
            };
        } else {
            return {
                containerHeight: '200px',  // Increased to fit content
                avatarSize: 48,
                reviewFontSize: '0.85rem',
                nameFontSize: '0.9rem',
                titleFontSize: '0.75rem',
                quoteFontSize: '1.5rem',
                quoteOffset: -10,
                lineHeight: 1.45,
                padding: 2,
                paddingX: 3
            };
        }
    };

    const compactStyles = getCompactStyles();

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: compact ? '100%' : '800px',
                minHeight: compact ? (compactStyles?.containerHeight || '160px') : '550px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: compact ? (compactStyles?.padding || 2) : 4,
                px: compact ? (compactStyles?.paddingX || 3) : 4,
                position: 'relative',
                textAlign: 'center'
            }}
        >
            {/* Generous fixed height container to prevent ANY layout shifts during transition */}
            <Box sx={{
                width: '100%',
                height: compact ? (compactStyles?.containerHeight || '160px') : '350px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
            }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentReview.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '100%',
                            position: 'absolute'
                        }}
                    >
                        {/* Avatar */}
                        <Avatar
                            src={currentReview.avatar}
                            sx={{
                                width: compact ? (compactStyles?.avatarSize || 48) : 60,
                                height: compact ? (compactStyles?.avatarSize || 48) : 60,
                                mb: compact ? 0.5 : 2,
                                mt: 0,
                                border: compact ? '2px solid white' : '5px solid white',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                            }}
                        />

                        {/* Review Text with Highlights */}
                        <Box sx={{ position: 'relative', px: compact ? (compactStyles?.paddingX || 2) : 8, mb: compact ? 0.5 : 5 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#334155',
                                    fontWeight: 500,
                                    lineHeight: compact ? (compactStyles?.lineHeight || 1.4) : 2,
                                    fontStyle: 'italic',
                                    fontSize: compact ? (compactStyles?.reviewFontSize || '0.8rem') : '2.5rem !important',
                                    position: 'relative',
                                    display: 'inline-block'
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: compact ? (compactStyles?.quoteFontSize || '1.5rem') : '3rem',
                                        color: '#0051C3',
                                        opacity: compact ? 0.25 : 0.4,
                                        fontFamily: 'serif',
                                        position: 'absolute',
                                        left: compact ? (compactStyles?.quoteOffset || -8) : -25,
                                        top: compact ? -3 : -10,
                                        lineHeight: 1
                                    }}
                                >
                                    "
                                </Box>
                                {renderText(currentReview.text, currentReview.highlightText)}
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: compact ? (compactStyles?.quoteFontSize || '1.5rem') : '3rem',
                                        color: '#0051C3 !important',
                                        opacity: compact ? 0.25 : 0.4,
                                        fontFamily: 'serif',
                                        position: 'absolute',
                                        right: compact ? (compactStyles?.quoteOffset || -8) : -25,
                                        bottom: compact ? -3 : -15,
                                        lineHeight: 1
                                    }}
                                >
                                    "
                                </Box>
                            </Typography>
                        </Box>

                        {/* Attribution */}
                        <Box sx={{ mt: compact ? 0.25 : 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    mb: 0.15,
                                    fontSize: compact ? (compactStyles?.nameFontSize || '0.85rem') : '1.4rem'
                                }}
                            >
                                {currentReview.name}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#64748b',
                                    fontWeight: 500,
                                    fontSize: compact ? (compactStyles?.titleFontSize || '0.75rem') : '1rem'
                                }}
                            >
                                {currentReview.title}
                            </Typography>
                        </Box>
                    </motion.div>
                </AnimatePresence>
            </Box>

            {/* Pagination dots - fixed vertical position */}
            <Box sx={{
                display: 'flex',
                gap: compact ? 0.6 : 1.2,
                mt: compact ? 1.5 : 1,  // More margin-top for proper spacing
                justifyContent: 'center',
                alignItems: 'center',
                height: compact ? '14px' : '16px'
            }}>
                {reviews.map((_, i) => (
                    <Box
                        key={i}
                        onClick={() => setIndex(i)}
                        sx={{
                            width: i === index ? (compact ? 12 : 24) : (compact ? 4 : 8),
                            height: compact ? 4 : 8,
                            borderRadius: '4px',
                            bgcolor: i === index ? '#0051C3' : '#cbd5e1',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: i === index ? '#0051C3' : '#94a3b8'
                            }
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default ReviewCarousel;
